
import { SQLModule } from '../types';

// Additional modules for SQL Fundamentals course
export const SQL_FUNDAMENTALS_EXPANDED_MODULES: SQLModule[] = [
  {
    id: 'sql-basics-4',
    title: 'Data Types and Constraints',
    description: 'Learn about different SQL data types and how to enforce data integrity with constraints.',
    content: `# Data Types and Constraints

## SQL Data Types

Understanding data types is crucial for designing efficient and reliable databases.

### Numeric Data Types

#### Integer Types
\`\`\`sql
-- Different integer types
CREATE TABLE numeric_examples (
    tiny_num TINYINT,        -- -128 to 127
    small_num SMALLINT,      -- -32,768 to 32,767
    medium_num MEDIUMINT,    -- -8,388,608 to 8,388,607
    regular_num INT,         -- -2,147,483,648 to 2,147,483,647
    big_num BIGINT          -- Very large range
);
\`\`\`

#### Decimal and Float Types
\`\`\`sql
-- Precise decimal numbers
CREATE TABLE financial_data (
    product_id INT,
    price DECIMAL(10,2),     -- 10 digits total, 2 after decimal
    weight FLOAT,            -- Approximate floating point
    precise_weight DOUBLE    -- Double precision floating point
);
\`\`\`

### String Data Types

\`\`\`sql
CREATE TABLE text_examples (
    id INT PRIMARY KEY,
    fixed_code CHAR(5),      -- Always 5 characters
    name VARCHAR(100),       -- Variable length, max 100
    description TEXT,        -- Large text data
    notes LONGTEXT          -- Very large text data
);
\`\`\`

### Date and Time Types

\`\`\`sql
CREATE TABLE datetime_examples (
    id INT PRIMARY KEY,
    birth_date DATE,                    -- YYYY-MM-DD
    login_time TIME,                    -- HH:MM:SS
    created_at DATETIME,                -- YYYY-MM-DD HH:MM:SS
    updated_at TIMESTAMP DEFAULT CURRENT_TIMESTAMP ON UPDATE CURRENT_TIMESTAMP,
    event_year YEAR                     -- YYYY
);
\`\`\`

## Database Constraints

Constraints ensure data integrity and enforce business rules.

### Primary Key Constraint

\`\`\`sql
-- Single column primary key
CREATE TABLE students (
    student_id INT PRIMARY KEY,
    first_name VARCHAR(50) NOT NULL,
    last_name VARCHAR(50) NOT NULL
);

-- Composite primary key
CREATE TABLE enrollment (
    student_id INT,
    course_id INT,
    enrollment_date DATE,
    PRIMARY KEY (student_id, course_id)
);
\`\`\`

### Foreign Key Constraint

\`\`\`sql
CREATE TABLE courses (
    course_id INT PRIMARY KEY,
    course_name VARCHAR(100) NOT NULL,
    department_id INT,
    FOREIGN KEY (department_id) REFERENCES departments(dept_id)
        ON DELETE SET NULL
        ON UPDATE CASCADE
);
\`\`\`

### Unique Constraint

\`\`\`sql
CREATE TABLE users (
    user_id INT PRIMARY KEY,
    username VARCHAR(50) UNIQUE,
    email VARCHAR(100) UNIQUE,
    phone VARCHAR(15),
    UNIQUE (phone, email)  -- Composite unique constraint
);
\`\`\`

### Check Constraint

\`\`\`sql
CREATE TABLE products (
    product_id INT PRIMARY KEY,
    product_name VARCHAR(100) NOT NULL,
    price DECIMAL(10,2) CHECK (price > 0),
    category VARCHAR(50) CHECK (category IN ('Electronics', 'Clothing', 'Books')),
    stock_quantity INT CHECK (stock_quantity >= 0)
);
\`\`\`

### NOT NULL Constraint

\`\`\`sql
CREATE TABLE employees (
    emp_id INT PRIMARY KEY,
    first_name VARCHAR(50) NOT NULL,
    last_name VARCHAR(50) NOT NULL,
    email VARCHAR(100) NOT NULL UNIQUE,
    hire_date DATE NOT NULL,
    salary DECIMAL(10,2)  -- Can be NULL
);
\`\`\`

## Practical Examples

### Creating a Complete Table with Constraints

\`\`\`sql
CREATE TABLE orders (
    order_id INT AUTO_INCREMENT PRIMARY KEY,
    customer_id INT NOT NULL,
    order_date DATE NOT NULL DEFAULT (CURRENT_DATE),
    order_total DECIMAL(12,2) CHECK (order_total >= 0),
    order_status ENUM('pending', 'processing', 'shipped', 'delivered', 'cancelled') 
        DEFAULT 'pending',
    shipping_address TEXT NOT NULL,
    
    FOREIGN KEY (customer_id) REFERENCES customers(customer_id)
        ON DELETE RESTRICT
        ON UPDATE CASCADE,
        
    INDEX idx_customer_date (customer_id, order_date),
    INDEX idx_status (order_status)
);
\`\`\`

### Modifying Constraints

\`\`\`sql
-- Add constraint to existing table
ALTER TABLE products 
ADD CONSTRAINT chk_price_positive CHECK (price > 0);

-- Drop constraint
ALTER TABLE products 
DROP CONSTRAINT chk_price_positive;

-- Add foreign key
ALTER TABLE orders 
ADD FOREIGN KEY (customer_id) REFERENCES customers(customer_id);
\`\`\``,
    duration: '60 min',
    estimatedTime: 60,
    difficulty: 'beginner',
    order: 4
  },
  {
    id: 'sql-basics-5',
    title: 'INSERT, UPDATE, DELETE Operations',
    description: 'Master data manipulation operations to insert, update, and delete records effectively.',
    content: `# Data Manipulation Operations

## INSERT Operations

### Basic INSERT Syntax

\`\`\`sql
-- Insert single record
INSERT INTO students (first_name, last_name, email, birth_date)
VALUES ('John', 'Doe', 'john.doe@email.com', '1995-03-15');

-- Insert multiple records
INSERT INTO students (first_name, last_name, email, birth_date)
VALUES 
    ('Jane', 'Smith', 'jane.smith@email.com', '1996-07-22'),
    ('Mike', 'Johnson', 'mike.johnson@email.com', '1994-11-08'),
    ('Sarah', 'Wilson', 'sarah.wilson@email.com', '1997-02-14');
\`\`\`

### INSERT with Default Values

\`\`\`sql
-- Using DEFAULT keyword
INSERT INTO products (product_name, price, created_at)
VALUES ('New Product', 29.99, DEFAULT);

-- Omitting columns with defaults
INSERT INTO products (product_name, price)
VALUES ('Another Product', 19.99);
\`\`\`

### INSERT from SELECT

\`\`\`sql
-- Copy data from one table to another
INSERT INTO archived_orders (order_id, customer_id, order_date, total)
SELECT order_id, customer_id, order_date, order_total
FROM orders
WHERE order_date < '2023-01-01';

-- Insert with calculations
INSERT INTO monthly_sales (month, year, total_sales)
SELECT 
    MONTH(order_date) as month,
    YEAR(order_date) as year,
    SUM(order_total) as total_sales
FROM orders
WHERE YEAR(order_date) = 2024
GROUP BY YEAR(order_date), MONTH(order_date);
\`\`\`

## UPDATE Operations

### Basic UPDATE Syntax

\`\`\`sql
-- Update single record
UPDATE students 
SET email = 'john.doe.new@email.com'
WHERE student_id = 1;

-- Update multiple columns
UPDATE products 
SET price = price * 1.1, 
    updated_at = CURRENT_TIMESTAMP
WHERE category = 'Electronics';
\`\`\`

### Conditional Updates

\`\`\`sql
-- Update with complex conditions
UPDATE employees 
SET salary = salary * 1.05
WHERE department = 'Sales' 
    AND hire_date < '2020-01-01'
    AND performance_rating >= 4;

-- Update using CASE statement
UPDATE orders 
SET shipping_cost = CASE 
    WHEN order_total > 100 THEN 0
    WHEN order_total > 50 THEN 5.99
    ELSE 9.99
END
WHERE shipping_cost IS NULL;
\`\`\`

### UPDATE with JOIN

\`\`\`sql
-- Update using data from another table
UPDATE customers c
JOIN (
    SELECT customer_id, COUNT(*) as order_count
    FROM orders
    GROUP BY customer_id
) o ON c.customer_id = o.customer_id
SET c.total_orders = o.order_count;
\`\`\`

## DELETE Operations

### Basic DELETE Syntax

\`\`\`sql
-- Delete specific records
DELETE FROM students 
WHERE graduation_date < '2020-01-01';

-- Delete with multiple conditions
DELETE FROM products 
WHERE stock_quantity = 0 
    AND last_sold_date < DATE_SUB(CURRENT_DATE, INTERVAL 1 YEAR);
\`\`\`

### Safe DELETE Practices

\`\`\`sql
-- Always test with SELECT first
SELECT * FROM orders 
WHERE order_date < '2020-01-01' 
    AND order_status = 'cancelled';

-- Then perform the DELETE
DELETE FROM orders 
WHERE order_date < '2020-01-01' 
    AND order_status = 'cancelled';
\`\`\`

### DELETE with Subqueries

\`\`\`sql
-- Delete records based on related data
DELETE FROM order_items 
WHERE order_id IN (
    SELECT order_id FROM orders 
    WHERE order_status = 'cancelled'
);

-- Delete duplicates (keeping one copy)
DELETE o1 FROM orders o1
JOIN orders o2 
WHERE o1.customer_id = o2.customer_id 
    AND o1.order_date = o2.order_date 
    AND o1.order_id > o2.order_id;
\`\`\`

## Transaction Safety

### Using Transactions

\`\`\`sql
-- Safe way to perform multiple operations
START TRANSACTION;

INSERT INTO customers (first_name, last_name, email)
VALUES ('New', 'Customer', 'new.customer@email.com');

SET @customer_id = LAST_INSERT_ID();

INSERT INTO orders (customer_id, order_total)
VALUES (@customer_id, 99.99);

-- If everything looks good
COMMIT;

-- If something went wrong
-- ROLLBACK;
\`\`\`

### Error Handling

\`\`\`sql
-- Check affected rows
UPDATE products SET price = price * 0.9 WHERE category = 'Clearance';
SELECT ROW_COUNT() as rows_updated;

-- Backup before major changes
CREATE TABLE customers_backup AS SELECT * FROM customers;

-- Perform risky operation
DELETE FROM customers WHERE last_order_date < '2020-01-01';

-- If needed, restore from backup
-- INSERT INTO customers SELECT * FROM customers_backup;
\`\`\``,
    duration: '55 min',
    estimatedTime: 55,
    difficulty: 'beginner',
    order: 5
  },
  {
    id: 'sql-basics-6',
    title: 'Database Design Basics',
    description: 'Learn fundamental database design principles including normalization and relationships.',
    content: `# Database Design Fundamentals

## Database Design Process

### 1. Requirements Analysis

Before creating tables, understand what data you need to store and how it will be used.

\`\`\`sql
-- Example: Online Bookstore Requirements
-- - Customers can place orders
-- - Orders contain multiple books
-- - Books have authors and categories
-- - Track inventory and pricing
\`\`\`

### 2. Entity Relationship Design

Identify entities and their relationships:

\`\`\`sql
-- Entities: Customer, Order, Book, Author, Category
-- Relationships:
-- - Customer places Orders (1:Many)
-- - Order contains Books (Many:Many)
-- - Book written by Authors (Many:Many)
-- - Book belongs to Category (Many:1)
\`\`\`

## Normalization

### First Normal Form (1NF)

Eliminate repeating groups and ensure atomic values.

\`\`\`sql
-- BAD: Not in 1NF (repeating groups)
CREATE TABLE bad_orders (
    order_id INT,
    customer_name VARCHAR(100),
    book1 VARCHAR(100),
    book2 VARCHAR(100),
    book3 VARCHAR(100)
);

-- GOOD: 1NF compliant
CREATE TABLE customers (
    customer_id INT PRIMARY KEY,
    customer_name VARCHAR(100) NOT NULL
);

CREATE TABLE orders (
    order_id INT PRIMARY KEY,
    customer_id INT,
    order_date DATE,
    FOREIGN KEY (customer_id) REFERENCES customers(customer_id)
);

CREATE TABLE order_items (
    order_id INT,
    book_id INT,
    quantity INT,
    PRIMARY KEY (order_id, book_id)
);
\`\`\`

### Second Normal Form (2NF)

Remove partial dependencies on composite keys.

\`\`\`sql
-- BAD: Not in 2NF (book_title depends only on book_id, not the full key)
CREATE TABLE bad_order_items (
    order_id INT,
    book_id INT,
    book_title VARCHAR(200),  -- Partial dependency
    quantity INT,
    PRIMARY KEY (order_id, book_id)
);

-- GOOD: 2NF compliant
CREATE TABLE books (
    book_id INT PRIMARY KEY,
    book_title VARCHAR(200) NOT NULL,
    isbn VARCHAR(13) UNIQUE,
    price DECIMAL(10,2)
);

CREATE TABLE order_items (
    order_id INT,
    book_id INT,
    quantity INT,
    unit_price DECIMAL(10,2),
    PRIMARY KEY (order_id, book_id),
    FOREIGN KEY (order_id) REFERENCES orders(order_id),
    FOREIGN KEY (book_id) REFERENCES books(book_id)
);
\`\`\`

### Third Normal Form (3NF)

Remove transitive dependencies.

\`\`\`sql
-- BAD: Not in 3NF (category_name depends on category_id, not book_id)
CREATE TABLE bad_books (
    book_id INT PRIMARY KEY,
    book_title VARCHAR(200),
    category_id INT,
    category_name VARCHAR(100)  -- Transitive dependency
);

-- GOOD: 3NF compliant
CREATE TABLE categories (
    category_id INT PRIMARY KEY,
    category_name VARCHAR(100) NOT NULL
);

CREATE TABLE books (
    book_id INT PRIMARY KEY,
    book_title VARCHAR(200) NOT NULL,
    category_id INT,
    price DECIMAL(10,2),
    FOREIGN KEY (category_id) REFERENCES categories(category_id)
);
\`\`\`

## Relationship Types

### One-to-Many Relationship

\`\`\`sql
-- Customer to Orders (1:Many)
CREATE TABLE customers (
    customer_id INT PRIMARY KEY AUTO_INCREMENT,
    first_name VARCHAR(50) NOT NULL,
    last_name VARCHAR(50) NOT NULL,
    email VARCHAR(100) UNIQUE NOT NULL
);

CREATE TABLE orders (
    order_id INT PRIMARY KEY AUTO_INCREMENT,
    customer_id INT NOT NULL,
    order_date DATE NOT NULL,
    total_amount DECIMAL(10,2),
    FOREIGN KEY (customer_id) REFERENCES customers(customer_id)
);
\`\`\`

### Many-to-Many Relationship

\`\`\`sql
-- Books to Authors (Many:Many)
CREATE TABLE authors (
    author_id INT PRIMARY KEY AUTO_INCREMENT,
    first_name VARCHAR(50) NOT NULL,
    last_name VARCHAR(50) NOT NULL
);

CREATE TABLE books (
    book_id INT PRIMARY KEY AUTO_INCREMENT,
    title VARCHAR(200) NOT NULL,
    isbn VARCHAR(13) UNIQUE,
    publication_date DATE
);

-- Junction table for Many:Many relationship
CREATE TABLE book_authors (
    book_id INT,
    author_id INT,
    author_role ENUM('primary', 'co-author', 'editor') DEFAULT 'primary',
    PRIMARY KEY (book_id, author_id),
    FOREIGN KEY (book_id) REFERENCES books(book_id) ON DELETE CASCADE,
    FOREIGN KEY (author_id) REFERENCES authors(author_id) ON DELETE CASCADE
);
\`\`\`

### One-to-One Relationship

\`\`\`sql
-- Customer to Customer Profile (1:1)
CREATE TABLE customers (
    customer_id INT PRIMARY KEY AUTO_INCREMENT,
    email VARCHAR(100) UNIQUE NOT NULL,
    password_hash VARCHAR(255) NOT NULL
);

CREATE TABLE customer_profiles (
    customer_id INT PRIMARY KEY,
    first_name VARCHAR(50),
    last_name VARCHAR(50),
    phone VARCHAR(20),
    birth_date DATE,
    created_at TIMESTAMP DEFAULT CURRENT_TIMESTAMP,
    FOREIGN KEY (customer_id) REFERENCES customers(customer_id) ON DELETE CASCADE
);
\`\`\`

## Complete Database Schema Example

\`\`\`sql
-- Complete Online Bookstore Schema
CREATE DATABASE bookstore;
USE bookstore;

-- Categories table
CREATE TABLE categories (
    category_id INT PRIMARY KEY AUTO_INCREMENT,
    category_name VARCHAR(100) NOT NULL UNIQUE,
    description TEXT
);

-- Authors table
CREATE TABLE authors (
    author_id INT PRIMARY KEY AUTO_INCREMENT,
    first_name VARCHAR(50) NOT NULL,
    last_name VARCHAR(50) NOT NULL,
    bio TEXT,
    birth_date DATE
);

-- Books table
CREATE TABLE books (
    book_id INT PRIMARY KEY AUTO_INCREMENT,
    title VARCHAR(200) NOT NULL,
    isbn VARCHAR(13) UNIQUE,
    category_id INT,
    price DECIMAL(10,2) NOT NULL CHECK (price >= 0),
    stock_quantity INT NOT NULL DEFAULT 0 CHECK (stock_quantity >= 0),
    publication_date DATE,
    page_count INT,
    description TEXT,
    created_at TIMESTAMP DEFAULT CURRENT_TIMESTAMP,
    FOREIGN KEY (category_id) REFERENCES categories(category_id)
);

-- Book-Author relationship
CREATE TABLE book_authors (
    book_id INT,
    author_id INT,
    author_role ENUM('primary', 'co-author', 'editor') DEFAULT 'primary',
    PRIMARY KEY (book_id, author_id),
    FOREIGN KEY (book_id) REFERENCES books(book_id) ON DELETE CASCADE,
    FOREIGN KEY (author_id) REFERENCES authors(author_id) ON DELETE CASCADE
);

-- Customers table
CREATE TABLE customers (
    customer_id INT PRIMARY KEY AUTO_INCREMENT,
    email VARCHAR(100) UNIQUE NOT NULL,
    password_hash VARCHAR(255) NOT NULL,
    first_name VARCHAR(50) NOT NULL,
    last_name VARCHAR(50) NOT NULL,
    phone VARCHAR(20),
    created_at TIMESTAMP DEFAULT CURRENT_TIMESTAMP,
    is_active BOOLEAN DEFAULT TRUE
);

-- Orders table
CREATE TABLE orders (
    order_id INT PRIMARY KEY AUTO_INCREMENT,
    customer_id INT NOT NULL,
    order_date TIMESTAMP DEFAULT CURRENT_TIMESTAMP,
    total_amount DECIMAL(12,2) NOT NULL CHECK (total_amount >= 0),
    order_status ENUM('pending', 'processing', 'shipped', 'delivered', 'cancelled') DEFAULT 'pending',
    shipping_address TEXT NOT NULL,
    FOREIGN KEY (customer_id) REFERENCES customers(customer_id)
);

-- Order Items table
CREATE TABLE order_items (
    order_id INT,
    book_id INT,
    quantity INT NOT NULL CHECK (quantity > 0),
    unit_price DECIMAL(10,2) NOT NULL CHECK (unit_price >= 0),
    PRIMARY KEY (order_id, book_id),
    FOREIGN KEY (order_id) REFERENCES orders(order_id) ON DELETE CASCADE,
    FOREIGN KEY (book_id) REFERENCES books(book_id)
);

-- Indexes for better performance
CREATE INDEX idx_books_category ON books(category_id);
CREATE INDEX idx_books_price ON books(price);
CREATE INDEX idx_orders_customer ON orders(customer_id);
CREATE INDEX idx_orders_date ON orders(order_date);
CREATE INDEX idx_orders_status ON orders(order_status);
\`\`\``,
    duration: '65 min',
    estimatedTime: 65,
    difficulty: 'beginner',
    order: 6
  },
  {
    id: 'sql-basics-7',
    title: 'Built-in SQL Functions',
    description: 'Master essential SQL functions for string manipulation, date operations, and mathematical calculations.',
    content: `# Built-in SQL Functions

## String Functions

### Basic String Operations

\`\`\`sql
-- String length and case conversion
SELECT 
    first_name,
    LENGTH(first_name) as name_length,
    UPPER(first_name) as uppercase_name,
    LOWER(first_name) as lowercase_name,
    CONCAT(first_name, ' ', last_name) as full_name
FROM customers;

-- String trimming and padding
SELECT 
    product_name,
    TRIM(product_name) as trimmed_name,
    LTRIM(product_name) as left_trimmed,
    RTRIM(product_name) as right_trimmed,
    LPAD(product_name, 20, '*') as left_padded,
    RPAD(product_name, 20, '-') as right_padded
FROM products;
\`\`\`

### String Extraction and Replacement

\`\`\`sql
-- Substring operations
SELECT 
    email,
    SUBSTRING(email, 1, LOCATE('@', email) - 1) as username,
    SUBSTRING(email, LOCATE('@', email) + 1) as domain,
    LEFT(email, 5) as first_5_chars,
    RIGHT(email, 4) as last_4_chars
FROM customers;

-- String replacement
SELECT 
    product_name,
    REPLACE(product_name, 'Smartphone', 'Mobile Phone') as updated_name,
    REVERSE(product_name) as reversed_name
FROM products
WHERE product_name LIKE '%Smartphone%';
\`\`\`

### Advanced String Functions

\`\`\`sql
-- String searching and manipulation
SELECT 
    description,
    LOCATE('premium', LOWER(description)) as premium_position,
    SUBSTRING_INDEX(description, ' ', 3) as first_3_words,
    CHAR_LENGTH(description) as char_count,
    REPEAT('*', 5) as stars
FROM products;

-- Regular expression functions (MySQL 8.0+)
SELECT 
    phone_number,
    REGEXP_REPLACE(phone_number, '[^0-9]', '') as digits_only,
    CASE 
        WHEN phone_number REGEXP '^[0-9]{3}-[0-9]{3}-[0-9]{4}$' 
        THEN 'Valid Format'
        ELSE 'Invalid Format'
    END as format_check
FROM customers;
\`\`\`

## Date and Time Functions

### Current Date and Time

\`\`\`sql
-- Current date/time functions
SELECT 
    NOW() as current_datetime,
    CURDATE() as current_date,
    CURTIME() as current_time,
    UNIX_TIMESTAMP() as unix_timestamp,
    UTC_TIMESTAMP() as utc_time;
\`\`\`

### Date Extraction

\`\`\`sql
-- Extract parts from dates
SELECT 
    order_date,
    YEAR(order_date) as order_year,
    MONTH(order_date) as order_month,
    DAY(order_date) as order_day,
    DAYOFWEEK(order_date) as day_of_week,
    DAYNAME(order_date) as day_name,
    MONTHNAME(order_date) as month_name,
    QUARTER(order_date) as quarter
FROM orders;
\`\`\`

### Date Arithmetic

\`\`\`sql
-- Date calculations
SELECT 
    customer_id,
    created_at,
    DATE_ADD(created_at, INTERVAL 30 DAY) as plus_30_days,
    DATE_SUB(created_at, INTERVAL 1 YEAR) as minus_1_year,
    DATEDIFF(NOW(), created_at) as days_since_creation,
    TIMESTAMPDIFF(MONTH, created_at, NOW()) as months_since_creation
FROM customers;

-- Age calculation
SELECT 
    first_name,
    birth_date,
    TIMESTAMPDIFF(YEAR, birth_date, CURDATE()) as age,
    CASE 
        WHEN TIMESTAMPDIFF(YEAR, birth_date, CURDATE()) >= 18 
        THEN 'Adult' 
        ELSE 'Minor' 
    END as age_category
FROM customers
WHERE birth_date IS NOT NULL;
\`\`\`

### Date Formatting

\`\`\`sql
-- Format dates
SELECT 
    order_date,
    DATE_FORMAT(order_date, '%Y-%m-%d') as iso_format,
    DATE_FORMAT(order_date, '%M %d, %Y') as readable_format,
    DATE_FORMAT(order_date, '%W, %M %e, %Y') as full_format,
    TIME_FORMAT(created_at, '%H:%i:%s') as time_only
FROM orders;
\`\`\`

## Mathematical Functions

### Basic Math Operations

\`\`\`sql
-- Basic mathematical functions
SELECT 
    price,
    ROUND(price) as rounded_price,
    ROUND(price, 1) as rounded_to_1_decimal,
    CEILING(price) as ceiling_price,
    FLOOR(price) as floor_price,
    ABS(price - 50) as distance_from_50,
    SQRT(price) as square_root,
    POWER(price, 2) as price_squared
FROM products;
\`\`\`

### Advanced Mathematical Functions

\`\`\`sql
-- Advanced math functions
SELECT 
    order_total,
    MOD(order_total, 10) as remainder_when_divided_by_10,
    SIGN(order_total - 100) as comparison_to_100,
    LOG(order_total) as natural_log,
    LOG10(order_total) as log_base_10,
    EXP(1) as euler_number,
    PI() as pi_value,
    RAND() as random_number,
    RAND() * 100 as random_percentage
FROM orders;
\`\`\`

## Conditional Functions

### CASE Statements

\`\`\`sql
-- Simple CASE
SELECT 
    product_name,
    price,
    CASE 
        WHEN price < 20 THEN 'Budget'
        WHEN price BETWEEN 20 AND 100 THEN 'Mid-range'
        WHEN price > 100 THEN 'Premium'
        ELSE 'Unknown'
    END as price_category
FROM products;

-- Searched CASE with multiple conditions
SELECT 
    customer_id,
    order_date,
    total_amount,
    CASE 
        WHEN total_amount > 500 AND YEAR(order_date) = 2024 THEN 'VIP Current'
        WHEN total_amount > 500 THEN 'VIP Legacy'
        WHEN total_amount > 100 THEN 'Premium'
        ELSE 'Standard'
    END as customer_tier
FROM orders;
\`\`\`

### NULL Handling Functions

\`\`\`sql
-- Handle NULL values
SELECT 
    customer_id,
    first_name,
    last_name,
    phone,
    IFNULL(phone, 'No phone provided') as phone_display,
    COALESCE(phone, email, 'No contact info') as primary_contact,
    NULLIF(phone, '') as clean_phone,  -- Convert empty string to NULL
    IF(phone IS NOT NULL, 'Has Phone', 'No Phone') as phone_status
FROM customers;
\`\`\`

## Practical Examples

### Customer Analysis

\`\`\`sql
-- Comprehensive customer analysis using multiple functions
SELECT 
    customer_id,
    CONCAT(UPPER(LEFT(first_name, 1)), LOWER(SUBSTRING(first_name, 2))) as formatted_first_name,
    CONCAT(UPPER(LEFT(last_name, 1)), LOWER(SUBSTRING(last_name, 2))) as formatted_last_name,
    email,
    SUBSTRING(email, LOCATE('@', email) + 1) as email_domain,
    created_at,
    DATE_FORMAT(created_at, '%M %Y') as join_month_year,
    TIMESTAMPDIFF(DAY, created_at, NOW()) as days_as_customer,
    CASE 
        WHEN TIMESTAMPDIFF(DAY, created_at, NOW()) > 365 THEN 'Loyal Customer'
        WHEN TIMESTAMPDIFF(DAY, created_at, NOW()) > 90 THEN 'Regular Customer'
        ELSE 'New Customer'
    END as customer_type
FROM customers
ORDER BY created_at DESC;
\`\`\`

### Sales Report with Functions

\`\`\`sql
-- Monthly sales report with calculations
SELECT 
    YEAR(order_date) as year,
    MONTHNAME(order_date) as month,
    COUNT(*) as total_orders,
    SUM(total_amount) as total_sales,
    ROUND(AVG(total_amount), 2) as average_order_value,
    MIN(total_amount) as smallest_order,
    MAX(total_amount) as largest_order,
    CONCAT('$', FORMAT(SUM(total_amount), 2)) as formatted_sales
FROM orders
WHERE order_status = 'completed'
    AND order_date >= DATE_SUB(NOW(), INTERVAL 12 MONTH)
GROUP BY YEAR(order_date), MONTH(order_date)
ORDER BY year DESC, MONTH(order_date) DESC;
\`\`\``,
    duration: '50 min',
    estimatedTime: 50,
    difficulty: 'beginner',
    order: 7
  },
  {
    id: 'sql-basics-8',
    title: 'LIMIT, Pagination and NULL Handling',
    description: 'Learn to control result sets with LIMIT, implement pagination, and handle NULL values effectively.',
    content: `# LIMIT, Pagination and NULL Handling

## LIMIT Clause

### Basic LIMIT Usage

\`\`\`sql
-- Get first 10 records
SELECT * FROM customers
ORDER BY created_at DESC
LIMIT 10;

-- Get top 5 highest priced products
SELECT product_name, price
FROM products
ORDER BY price DESC
LIMIT 5;

-- Get random sample of records
SELECT * FROM products
ORDER BY RAND()
LIMIT 3;
\`\`\`

### LIMIT with OFFSET

\`\`\`sql
-- Skip first 10 records, get next 10 (MySQL/PostgreSQL)
SELECT * FROM customers
ORDER BY customer_id
LIMIT 10 OFFSET 10;

-- Alternative syntax for MySQL
SELECT * FROM customers
ORDER BY customer_id
LIMIT 10, 10;  -- LIMIT offset, count
\`\`\`

## Pagination Implementation

### Basic Pagination

\`\`\`sql
-- Page 1 (first 20 records)
SELECT customer_id, first_name, last_name, email
FROM customers
ORDER BY customer_id
LIMIT 20 OFFSET 0;

-- Page 2 (next 20 records)
SELECT customer_id, first_name, last_name, email
FROM customers
ORDER BY customer_id
LIMIT 20 OFFSET 20;

-- Page 3 (next 20 records)
SELECT customer_id, first_name, last_name, email
FROM customers
ORDER BY customer_id
LIMIT 20 OFFSET 40;
\`\`\`

### Pagination with Page Calculation

\`\`\`sql
-- Generic pagination formula: OFFSET = (page_number - 1) * page_size
-- Page 5, showing 10 records per page
SET @page_number = 5;
SET @page_size = 10;
SET @offset = (@page_number - 1) * @page_size;

SELECT customer_id, first_name, last_name, email
FROM customers
ORDER BY customer_id
LIMIT @page_size OFFSET @offset;
\`\`\`

### Pagination with Total Count

\`\`\`sql
-- Get paginated results with total count information
SELECT 
    customer_id, 
    first_name, 
    last_name, 
    email,
    COUNT(*) OVER() as total_records
FROM customers
ORDER BY customer_id
LIMIT 10 OFFSET 20;

-- Or get total count separately
SELECT COUNT(*) as total_customers FROM customers;

SELECT customer_id, first_name, last_name, email
FROM customers
ORDER BY customer_id
LIMIT 10 OFFSET 20;
\`\`\`

### Cursor-based Pagination (Performance Optimized)

\`\`\`sql
-- First page (no cursor)
SELECT customer_id, first_name, last_name, email
FROM customers
ORDER BY customer_id
LIMIT 10;

-- Next page (using last customer_id as cursor)
SELECT customer_id, first_name, last_name, email
FROM customers
WHERE customer_id > 10  -- Last customer_id from previous page
ORDER BY customer_id
LIMIT 10;

-- For descending order
SELECT customer_id, first_name, last_name, email
FROM customers
WHERE customer_id < 50  -- Last customer_id from previous page
ORDER BY customer_id DESC
LIMIT 10;
\`\`\`

## NULL Handling

### Understanding NULL Values

\`\`\`sql
-- NULL represents missing or unknown data
-- NULL is not the same as empty string '' or zero 0

-- Find records with NULL values
SELECT * FROM customers
WHERE phone IS NULL;

-- Find records with non-NULL values
SELECT * FROM customers
WHERE phone IS NOT NULL;

-- Cannot use = or != with NULL
-- This will NOT work as expected:
-- SELECT * FROM customers WHERE phone = NULL;
\`\`\`

### NULL Functions

\`\`\`sql
-- IFNULL - replace NULL with a default value (MySQL)
SELECT 
    customer_id,
    first_name,
    IFNULL(phone, 'No phone provided') as phone_display
FROM customers;

-- COALESCE - return first non-NULL value (Standard SQL)
SELECT 
    customer_id,
    first_name,
    COALESCE(phone, mobile, email, 'No contact') as primary_contact
FROM customers;

-- NULLIF - return NULL if values are equal
SELECT 
    customer_id,
    first_name,
    NULLIF(phone, '') as clean_phone  -- Convert empty string to NULL
FROM customers;
\`\`\`

### NULL in Calculations

\`\`\`sql
-- NULL in calculations always results in NULL
SELECT 
    product_name,
    price,
    discount,
    price - discount as discounted_price,  -- NULL if discount is NULL
    price - IFNULL(discount, 0) as safe_discounted_price  -- Safe calculation
FROM products;

-- NULL in aggregations
SELECT 
    COUNT(*) as total_products,
    COUNT(discount) as products_with_discount,  -- Excludes NULLs
    AVG(discount) as average_discount,  -- Excludes NULLs
    SUM(IFNULL(discount, 0)) as total_discounts  -- Include NULLs as 0
FROM products;
\`\`\`

### NULL with Sorting

\`\`\`sql
-- NULL values in ORDER BY
-- In MySQL, NULL values come first in ASC, last in DESC

-- NULLs first (default for ASC)
SELECT customer_id, first_name, phone
FROM customers
ORDER BY phone ASC;

-- Force NULLs last in ASC
SELECT customer_id, first_name, phone
FROM customers
ORDER BY phone IS NULL, phone ASC;

-- Force NULLs first in DESC
SELECT customer_id, first_name, phone
FROM customers
ORDER BY phone IS NOT NULL, phone DESC;
\`\`\`

## Practical Examples

### Product Catalog with Pagination

\`\`\`sql
-- Product listing with pagination and NULL handling
SELECT 
    product_id,
    product_name,
    IFNULL(category_name, 'Uncategorized') as category,
    CONCAT('$', FORMAT(price, 2)) as formatted_price,
    CASE 
        WHEN stock_quantity IS NULL THEN 'Stock info unavailable'
        WHEN stock_quantity = 0 THEN 'Out of stock'
        WHEN stock_quantity < 10 THEN 'Low stock'
        ELSE 'In stock'
    END as stock_status
FROM products p
LEFT JOIN categories c ON p.category_id = c.category_id
ORDER BY 
    p.featured DESC,  -- Featured products first
    stock_quantity IS NULL,  -- Products with stock info first
    p.product_name
LIMIT 20 OFFSET 0;
\`\`\`

### Customer Report with NULL Handling

\`\`\`sql
-- Customer contact report handling various NULL scenarios
SELECT 
    customer_id,
    CONCAT(first_name, ' ', last_name) as full_name,
    email,
    CASE 
        WHEN phone IS NOT NULL AND phone != '' THEN phone
        WHEN mobile IS NOT NULL AND mobile != '' THEN CONCAT(mobile, ' (mobile)')
        ELSE 'No phone number'
    END as contact_phone,
    COALESCE(
        NULLIF(preferred_contact, ''),
        CASE WHEN phone IS NOT NULL THEN 'phone' ELSE 'email' END
    ) as contact_preference,
    IFNULL(last_login_date, 'Never logged in') as last_login_display,
    CASE 
        WHEN last_login_date IS NULL THEN 'Inactive'
        WHEN last_login_date > DATE_SUB(NOW(), INTERVAL 30 DAY) THEN 'Active'
        ELSE 'Dormant'
    END as activity_status
FROM customers
WHERE is_active = TRUE
ORDER BY 
    last_login_date IS NULL,  -- Active users first
    last_login_date DESC,
    customer_id
LIMIT 50;
\`\`\`

### Advanced Pagination with Filters

\`\`\`sql
-- Paginated product search with filters and NULL handling
SET @search_term = 'smartphone';
SET @min_price = 100;
SET @max_price = 1000;
SET @page_size = 20;
SET @page_number = 2;
SET @offset = (@page_number - 1) * @page_size;

SELECT 
    p.product_id,
    p.product_name,
    IFNULL(c.category_name, 'No Category') as category,
    p.price,
    IFNULL(p.stock_quantity, 0) as stock,
    COUNT(*) OVER() as total_matching_products
FROM products p
LEFT JOIN categories c ON p.category_id = c.category_id
WHERE 
    (p.product_name LIKE CONCAT('%', @search_term, '%') 
     OR p.description LIKE CONCAT('%', @search_term, '%'))
    AND p.price BETWEEN @min_price AND @max_price
    AND p.is_active = TRUE
    AND (p.stock_quantity IS NULL OR p.stock_quantity > 0)  -- Include unknown stock
ORDER BY 
    CASE WHEN p.featured = TRUE THEN 0 ELSE 1 END,  -- Featured first
    p.stock_quantity IS NULL,  -- Known stock first
    p.price ASC
LIMIT @page_size OFFSET @offset;
\`\`\`

### Performance Considerations

\`\`\`sql
-- Efficient pagination with proper indexing
-- Make sure to have appropriate indexes:

-- For customer pagination by ID
-- CREATE INDEX idx_customers_id ON customers(customer_id);

-- For product search with price filter
-- CREATE INDEX idx_products_search ON products(is_active, price, product_name);

-- For date-based pagination
-- CREATE INDEX idx_orders_date_customer ON orders(order_date, customer_id);

-- Avoid using OFFSET with large values (performance degrades)
-- Instead, use cursor-based pagination when possible
SELECT customer_id, first_name, last_name, created_at
FROM customers
WHERE customer_id > 1000  -- Last ID from previous page
ORDER BY customer_id
LIMIT 20;
\`\`\``,
    duration: '45 min',
    estimatedTime: 45,
    difficulty: 'beginner',
    order: 8
  }
];
